import { supabase } from '@/lib/supabase';
import type { AuthResponse } from './types';
import { SubscriptionService } from '@/services/subscriptionService';

export const createOrganization = async (
  userId: string,
  organizationName: string
): Promise<AuthResponse> => {
  try {
    console.log('[AUTH API] Creating organization via Edge Function:', organizationName, 'for user:', userId);
    
    if (!organizationName || organizationName.trim() === '') {
      return {
        data: null,
        error: new Error('Organization name is required'),
        status: 'error',
        message: 'Failed to create organization: Organization name is required'
      };
    }
    
    // Make sure we're using the correct Supabase instance
    if (!supabase) {
      console.error('[AUTH API] Supabase client not initialized');
      return {
        data: null,
        error: new Error('Supabase client not initialized'),
        status: 'error',
        message: 'Internal configuration error'
      };
    }

    // SECURITY: Verify we have a valid session before calling secured Edge Function
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.error('[AUTH API] No active session for Edge Function call');
      return {
        data: null,
        error: new Error('Authentication required'),
        status: 'error',
        message: 'Failed to create organization: Authentication required'
      };
    }
    
    console.log('[AUTH API] Authenticated session found, calling Edge Function...');
    
    // Call the edge function with explicit error handling
    const { data, error } = await supabase.functions.invoke('create-organization', {
      body: {
        userId,
        organizationName
      }
    });
    
    console.log('[AUTH API] Edge function response:', { data, error });
    
    if (error) {
      console.error('[AUTH API] Organization creation via Edge Function failed:', error);
      
      // Handle specific authentication/authorization errors
      if (error.message?.includes('Authentication required') || 
          error.message?.includes('authorization header')) {
        return {
          data: null,
          error,
          status: 'error',
          message: 'Authentication failed: Please ensure you are logged in'
        };
      }
      
      if (error.message?.includes('Insufficient permissions')) {
        return {
          data: null,
          error,
          status: 'error',
          message: 'Permission denied: You do not have sufficient privileges'
        };
      }
      
      return {
        data: null,
        error,
        status: 'error',
        message: 'Failed to create organization: ' + error.message
      };
    }
    
    const organizationId = data?.organizationId;
    if (!organizationId) {
      console.error('[AUTH API] No organizationId returned from edge function');
      return {
        data: null,
        error: new Error('No organizationId returned'),
        status: 'error',
        message: 'Failed to create organization: No organizationId returned'
      };
    }
    
    // Immediately initialize the trial for the new organization
    const trialDays = 14; // Or fetch from superadmin setting if needed
    const trialResult = await SubscriptionService.initializeTrial(organizationId, trialDays);
    if (!trialResult) {
      console.error('[AUTH API] Failed to initialize trial for new organization');
    }
    
    console.log('[AUTH API] Organization created successfully with ID:', organizationId);
    
    // Add extra log to trace organization creation flow
    console.log('[AUTH API] Organization creation complete, will update user metadata and profile now');
    
    return {
      data: { id: organizationId },
      error: null,
      status: 'success',
      message: 'Organization created successfully'
    };
  } catch (error) {
    console.error('[AUTH API] Unexpected error creating organization:', error);
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
      status: 'error',
      message: 'Failed to create organization: ' + (error instanceof Error ? error.message : String(error))
    };
  }
};
